import { AnalyticsFilters, DashboardData, UserMetrics, GroupMetrics, SystemMetrics } from "../../types/analytics";
import { FormTemplate } from "../../db/definitions";
import { fetchAnalyticsAggregates, processAggregatesIntoDashboard } from "./analyticsDataService";
import handleBatchGetFormTemplates from "../../utils/handleBatchGetFormTemplates";

export interface AnalyticsReportMetrics {
	// Time period
	reportPeriod: string;
	startDate: string;
	endDate: string;
	totalDays: number;

	// User Activity
	activeUsersPerDay: { date: string; count: number }[];
	totalActiveUsers: number;
	averageSessionTime: number; // minutes
	totalSessions: number;

	// Form Activity
	formTemplateIdsCreated: string[];
	totalFormsCreated: number;

	// User & Group Activity
	activeUserIds: string[];
	activeGroupIds: string[];

	// Project Activity
	activeProjectIds: string[];
	projectVisits: number;

	// Device Usage
	deviceBreakdown: { device: string; count: number; percentage: number }[];

	// Export Activity
	exportMetrics: {
		lastDay: number;
		lastWeek: number;
		lastMonth: number;
		lastQuarter: number;
		lastYear: number;
		perUser: { userId: string; count: number }[];
	};

	// History Activity
	totalHistoryReverts: number;
}

class AnalyticsReportService {
	/**
	 * Generate comprehensive analytics report
	 */
	async generateReport(filters?: AnalyticsFilters): Promise<AnalyticsReportMetrics> {
		// Use all available data as default
		const reportFilters = filters || {
			dateRange: undefined,
			groupId: undefined,
		};

		const analyticsResult = await fetchAnalyticsAggregates({ groupId: reportFilters.groupId });

		// Paginate through all results
		const allAggregates = [...analyticsResult.aggregates];
		let nextToken = analyticsResult.nextToken;
		while (nextToken) {
			const page = await fetchAnalyticsAggregates({ groupId: reportFilters.groupId, nextToken });
			allAggregates.push(...page.aggregates);
			nextToken = page.nextToken;
		}

		// Create a default date range if none provided
		const defaultDateRange = reportFilters.dateRange || {
			startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], // 30 days ago
			endDate: new Date().toISOString().split("T")[0], // today
		};

		const analyticsData = processAggregatesIntoDashboard(allAggregates, defaultDateRange);

		return this.processAnalyticsData(analyticsData, reportFilters);
	}

	/**
	 * Process raw analytics data into report metrics
	 */
	private processAnalyticsData(data: DashboardData, filters: AnalyticsFilters): AnalyticsReportMetrics {
		const { userMetrics, groupMetrics, systemMetrics } = data;

		// Calculate time period
		const now = new Date();
		const reportPeriod =
			filters.dateRange?.startDate && filters.dateRange?.endDate
				? `${filters.dateRange.startDate} to ${filters.dateRange.endDate}`
				: "All available data";

		// Calculate date bounds from data
		const allDates = [
			...userMetrics.map((u: UserMetrics) => this.extractDateFromPeriod(u.period)),
			...groupMetrics.map((g: GroupMetrics) => this.extractDateFromPeriod(g.period)),
			...systemMetrics.map((s: SystemMetrics) => this.extractDateFromPeriod(s.period)),
		]
			.filter(Boolean)
			.sort();

		const startDate = allDates[0] || now.toISOString().split("T")[0];
		const endDate = allDates[allDates.length - 1] || now.toISOString().split("T")[0];
		const totalDays = this.calculateDaysBetween(startDate, endDate);

		// Process user activity
		const activeUsersPerDay = this.calculateActiveUsersPerDay(userMetrics);
		const totalActiveUsers = new Set(userMetrics.map((u) => u.userId)).size;
		const { averageSessionTime, totalSessions } = this.calculateSessionMetrics(userMetrics);

		// Process form activity
		const formTemplateIdsCreated = this.extractUniqueFormTemplateIds(userMetrics);
		const totalFormsCreated = userMetrics.reduce((sum: number, u: UserMetrics) => sum + (u.formCreates || 0), 0);

		// Process user & group activity
		const activeUserIds = Array.from(new Set(userMetrics.map((u: UserMetrics) => u.userId)));
		const activeGroupIds = Array.from(new Set(groupMetrics.map((g: GroupMetrics) => g.groupId)));

		// Process project activity
		const activeProjectIds = this.extractActiveProjects(userMetrics);
		const projectVisits = this.calculateProjectVisits(userMetrics);

		// Process device usage
		const deviceBreakdown = this.calculateDeviceBreakdown(userMetrics);

		// Process export metrics
		const exportMetrics = this.calculateExportMetrics(userMetrics, now);

		// Process history activity
		const totalHistoryReverts = userMetrics.reduce((sum: number, u: UserMetrics) => sum + (u.historyReverts || 0), 0);

		return {
			reportPeriod,
			startDate,
			endDate,
			totalDays,
			activeUsersPerDay,
			totalActiveUsers,
			averageSessionTime,
			totalSessions,
			formTemplateIdsCreated,
			totalFormsCreated,
			activeUserIds,
			activeGroupIds,
			activeProjectIds,
			projectVisits,
			deviceBreakdown,
			exportMetrics,
			totalHistoryReverts,
		};
	}

	/**
	 * Generate markdown report from metrics
	 */
	async generateMarkdownReport(metrics: AnalyticsReportMetrics): Promise<string> {
		const {
			reportPeriod,
			startDate,
			endDate,
			totalDays,
			activeUsersPerDay,
			totalActiveUsers,
			averageSessionTime,
			totalSessions,
			formTemplateIdsCreated,
			totalFormsCreated,
			activeUserIds,
			activeGroupIds,
			activeProjectIds,
			projectVisits,
			deviceBreakdown,
			exportMetrics,
			totalHistoryReverts,
		} = metrics;

		const generatedAt = new Date().toLocaleString();

		// Generate form template list asynchronously
		const formTemplateList = await this.generateFormTemplateIdList(formTemplateIdsCreated);

		return `# Analytics Report

**Generated:** ${generatedAt}  
**Period:** ${reportPeriod}  
**Date Range:** ${startDate} to ${endDate} (${totalDays} days)

---

## 📊 Overview

- **Total Active Users:** ${totalActiveUsers}
- **Total Sessions:** ${totalSessions.toLocaleString()}
- **Average Session Time:** ${averageSessionTime.toFixed(1)} minutes
- **Total Forms Created:** ${totalFormsCreated.toLocaleString()}
- **Active Projects:** ${activeProjectIds.length}
- **Project Visits:** ${projectVisits.toLocaleString()}
- **History Reverts:** ${totalHistoryReverts.toLocaleString()}

---

## 👥 User Activity

### Daily Active Users
${this.generateDailyUsersTable(activeUsersPerDay)}

### Session Metrics
- **Total Sessions:** ${totalSessions.toLocaleString()}
- **Average Session Duration:** ${averageSessionTime.toFixed(1)} minutes
- **Average Sessions per User:** ${totalActiveUsers > 0 ? (totalSessions / totalActiveUsers).toFixed(1) : "0"}

### Active User IDs
${this.generateUserList(activeUserIds)}

---

## 📝 Form Activity

### Form Creation Summary
- **Total Forms Created:** ${totalFormsCreated.toLocaleString()}
- **Unique Form Template IDs:** ${formTemplateIdsCreated.length}

### Form Template IDs Created
${formTemplateList}

---

## ↩️ History Activity

### History Revert Summary
- **Total History Reverts:** ${totalHistoryReverts.toLocaleString()}
- **Average Reverts per User:** ${totalActiveUsers > 0 ? (totalHistoryReverts / totalActiveUsers).toFixed(1) : "0"}

*History reverts track when users use the undo functionality to revert field changes.*

---

## 🏢 Group Activity

### Active Groups
${this.generateGroupList(activeGroupIds)}

---

## 📱 Project Activity

### Active Projects  
- **Total Active Projects:** ${activeProjectIds.length}
- **Total Project Visits:** ${projectVisits.toLocaleString()}

### Project IDs Visited
${this.generateProjectList(activeProjectIds)}

---

## 📱 Device Usage

Note: Device counts represent individual events, not sessions.

${this.generateDeviceTable(deviceBreakdown)}

---

## 📤 Export Activity

### Export Summary by Time Period
- **Last 24 Hours:** ${exportMetrics.lastDay} exports
- **Last 7 Days:** ${exportMetrics.lastWeek} exports  
- **Last 30 Days:** ${exportMetrics.lastMonth} exports
- **Last Quarter (90 days):** ${exportMetrics.lastQuarter} exports
- **Last Year (365 days):** ${exportMetrics.lastYear} exports

### Exports Per User
${this.generateExportPerUserTable(exportMetrics.perUser)}

---

*Report generated from analytics aggregation data*
`;
	}

	/**
	 * Export markdown report as downloadable file
	 */
	async exportMarkdownFile(filters?: AnalyticsFilters): Promise<void> {
		const metrics = await this.generateReport(filters);
		const markdown = await this.generateMarkdownReport(metrics);

		const filename = `analytics-report-${new Date().toISOString().split("T")[0]}.md`;

		// Create and trigger download
		const blob = new Blob([markdown], { type: "text/markdown" });
		const url = URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		link.click();

		URL.revokeObjectURL(url);
	}

	// Helper methods for data processing

	private extractDateFromPeriod(period: string): string {
		// Handle different period formats (daily: YYYY-MM-DD, hourly: YYYY-MM-DD-HH)
		const parts = period.split("-");
		if (parts.length >= 3) {
			return `${parts[0]}-${parts[1]}-${parts[2]}`;
		}
		return new Date().toISOString().split("T")[0];
	}

	private calculateDaysBetween(startDate: string, endDate: string): number {
		const start = new Date(startDate);
		const end = new Date(endDate);
		const diffTime = Math.abs(end.getTime() - start.getTime());
		return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	}

	private calculateActiveUsersPerDay(userMetrics: UserMetrics[]): { date: string; count: number }[] {
		const dailyUsers = new Map<string, Set<string>>();

		userMetrics.forEach((metric: UserMetrics) => {
			const date = this.extractDateFromPeriod(metric.period);
			if (!dailyUsers.has(date)) {
				dailyUsers.set(date, new Set());
			}
			dailyUsers.get(date)?.add(metric.userId);
		});

		return Array.from(dailyUsers.entries())
			.map(([date, users]) => ({ date, count: users.size }))
			.sort((a, b) => a.date.localeCompare(b.date));
	}

	private calculateSessionMetrics(userMetrics: UserMetrics[]): { averageSessionTime: number; totalSessions: number } {
		let totalSessionTime = 0;
		let totalSessions = 0;

		userMetrics.forEach((metric: UserMetrics) => {
			// Use the actual computed session data from AnalyticsProcessor
			const sessionCount = metric.uniqueSessions || 0;
			const avgDuration = metric.averageSessionDuration || 0;
			const totalDuration = metric.totalSessionDuration || sessionCount * avgDuration;

			totalSessions += sessionCount;
			totalSessionTime += totalDuration;
		});

		const averageSessionTime = totalSessions > 0 ? totalSessionTime / totalSessions : 0;

		return { averageSessionTime, totalSessions };
	}

	private extractUniqueFormTemplateIds(userMetrics: UserMetrics[]): string[] {
		const templateIdSet = new Set<string>();

		userMetrics.forEach((metric: UserMetrics) => {
			if (metric.formTemplateIds) {
				metric.formTemplateIds.forEach((id: string) => templateIdSet.add(id));
			}
		});

		return Array.from(templateIdSet).sort();
	}

	private extractActiveProjects(userMetrics: UserMetrics[]): string[] {
		const projectIdSet = new Set<string>();

		userMetrics.forEach((metric: UserMetrics) => {
			if (metric.topUrls) {
				metric.topUrls.forEach((urlData: { url: string; count: number }) => {
					const match = urlData.url.match(/\/project\/([^\/\?]+)/);
					if (match) {
						projectIdSet.add(match[1]);
					}
				});
			}
		});

		return Array.from(projectIdSet).sort();
	}

	private calculateProjectVisits(userMetrics: UserMetrics[]): number {
		let totalVisits = 0;

		userMetrics.forEach((metric: UserMetrics) => {
			// Count explicit project_open events stored in the eventTypes breakdown.
			// Using topUrls URL counts is unreliable — it includes sub-route navigation
			// within a project and accumulates across all hourly aggregate records.
			const projectOpenCount = (metric.eventTypes as Record<string, number>)?.["project_open"] ?? 0;
			totalVisits += projectOpenCount;
		});

		return totalVisits;
	}

	private calculateDeviceBreakdown(userMetrics: UserMetrics[]): { device: string; count: number; percentage: number }[] {
		const deviceCounts = new Map<string, number>();
		let totalCount = 0;

		userMetrics.forEach((metric: UserMetrics) => {
			if (metric.deviceBreakdown) {
				Object.entries(metric.deviceBreakdown).forEach(([device, count]) => {
					const currentCount = deviceCounts.get(device) || 0;
					deviceCounts.set(device, currentCount + (count as number));
					totalCount += count as number;
				});
			}
		});

		return Array.from(deviceCounts.entries())
			.map(([device, count]) => ({
				device,
				count,
				percentage: totalCount > 0 ? (count / totalCount) * 100 : 0,
			}))
			.sort((a, b) => b.count - a.count);
	}

	private calculateExportMetrics(
		userMetrics: UserMetrics[],
		now: Date,
	): {
		lastDay: number;
		lastWeek: number;
		lastMonth: number;
		lastQuarter: number;
		lastYear: number;
		perUser: { userId: string; count: number }[];
	} {
		const nowMs = now.getTime();
		const oneDayMs = 24 * 60 * 60 * 1000;

		// Time boundaries
		const lastDay = nowMs - oneDayMs;
		const lastWeek = nowMs - 7 * oneDayMs;
		const lastMonth = nowMs - 30 * oneDayMs;
		const lastQuarter = nowMs - 90 * oneDayMs;
		const lastYear = nowMs - 365 * oneDayMs;

		let dayCount = 0,
			weekCount = 0,
			monthCount = 0,
			quarterCount = 0,
			yearCount = 0;
		const userExports = new Map<string, number>();

		userMetrics.forEach((metric: UserMetrics) => {
			const periodDate = new Date(this.extractDateFromPeriod(metric.period));
			const periodMs = periodDate.getTime();
			const exportCount = metric.exports || 0;

			// Add to user totals
			const currentUserCount = userExports.get(metric.userId) || 0;
			userExports.set(metric.userId, currentUserCount + exportCount);

			// Add to time period counts
			if (periodMs >= lastDay) dayCount += exportCount;
			if (periodMs >= lastWeek) weekCount += exportCount;
			if (periodMs >= lastMonth) monthCount += exportCount;
			if (periodMs >= lastQuarter) quarterCount += exportCount;
			if (periodMs >= lastYear) yearCount += exportCount;
		});

		// Convert user exports to array and sort
		const perUser = Array.from(userExports.entries())
			.map(([userId, count]) => ({ userId, count }))
			.sort((a, b) => b.count - a.count);

		return {
			lastDay: dayCount,
			lastWeek: weekCount,
			lastMonth: monthCount,
			lastQuarter: quarterCount,
			lastYear: yearCount,
			perUser,
		};
	}

	// Markdown generation helpers

	private generateDailyUsersTable(dailyUsers: { date: string; count: number }[]): string {
		if (dailyUsers.length === 0) return "_No daily user data available_";

		let table = "| Date | Active Users |\n| --- | --- |\n";
		dailyUsers.slice(-30).forEach((entry) => {
			// Show last 30 days
			table += `| ${entry.date} | ${entry.count} |\n`;
		});
		return table;
	}

	private generateUserList(userIds: string[]): string {
		if (userIds.length === 0) return "_No active users found_";

		const maxDisplay = 20;
		let list = "";

		userIds.slice(0, maxDisplay).forEach((userId) => {
			list += `- ${userId}\n`;
		});

		if (userIds.length > maxDisplay) {
			list += `\n_... and ${userIds.length - maxDisplay} more users_`;
		}

		return list;
	}

	private async generateFormTemplateIdList(templateIds: string[]): Promise<string> {
		if (templateIds.length === 0) return "_No forms created_";

		try {
			const formTemplates = await handleBatchGetFormTemplates(templateIds);
			if (formTemplates.length === 0) return "_No template details available_";

			let list = "";
			formTemplates.forEach((template: FormTemplate) => {
				if (!template) return;
				let templateName;
				if (template.templateName && template.templateName !== "") {
					templateName = template.templateName;
				} else {
					templateName = template.template;
				}
				list += `- ${templateName}\n`;
			});
			return list;
		} catch (error) {
			console.error("Error fetching form template details:", error);
			// Fallback to showing IDs if template fetch fails
			let list = "";
			templateIds.forEach((templateId) => {
				list += `- ${templateId}\n`;
			});
			return list;
		}
	}

	private generateGroupList(groupIds: string[]): string {
		if (groupIds.length === 0) return "_No active groups found_";

		let list = "";
		groupIds.forEach((groupId) => {
			list += `- ${groupId}\n`;
		});

		return list;
	}

	private generateProjectList(projectIds: string[]): string {
		if (projectIds.length === 0) return "_No projects visited_";

		let list = "";
		projectIds.forEach((projectId) => {
			list += `- ${projectId}\n`;
		});

		return list;
	}

	private generateDeviceTable(devices: { device: string; count: number; percentage: number }[]): string {
		if (devices.length === 0) return "_No device data available_";

		let table = "| Device | Events | Percentage |\n| --- | --- | --- |\n";
		devices.forEach((item) => {
			table += `| ${item.device} | ${item.count} | ${item.percentage.toFixed(1)}% |\n`;
		});

		return table;
	}

	private generateExportPerUserTable(userExports: { userId: string; count: number }[]): string {
		if (userExports.length === 0) return "_No export data available_";

		let table = "| User ID | Total Exports |\n| --- | --- |\n";
		const maxDisplay = 20;

		userExports.slice(0, maxDisplay).forEach((item) => {
			table += `| ${item.userId} | ${item.count} |\n`;
		});

		if (userExports.length > maxDisplay) {
			table += `\n_... and ${userExports.length - maxDisplay} more users_`;
		}

		return table;
	}
}

export const analyticsReportService = new AnalyticsReportService();
